import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import PageLoader from '@/app/common/components/PageLoader'
import { Badge } from '@/shad/ui/badge'
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { Label } from '@/shad/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shad/ui/tabs'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { useSelector } from 'react-redux'
import useGetPaymentRuleApplications from '../../../hooks/useGetPaymentApprovalRules'
import { ApprovalRule } from '../../../schema'
import { removeUnderscoresAndMakeFirstLetterCapital } from '../../../utils'

interface RuleDetailsContentProps {
  selectedRule: ApprovalRule
}

// Data display component that handles fetching and rendering
export function RuleDetailsContent({ selectedRule }: RuleDetailsContentProps) {
  const programId = useSelector(selectSelectedProgram).id
  const ruleTriggersQuery = useGetPaymentRuleApplications({
    programId,
    ruleId: selectedRule.id,
    options: {
      enabled: true,
    },
  })

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Rule Details</DialogTitle>
        <DialogDescription>
          Complete rule configuration and usage statistics
        </DialogDescription>
      </DialogHeader>
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="details">Configuration</TabsTrigger>
          <TabsTrigger value="usage">Usage Stats</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="space-y-6 pt-6">
          {/* Rule Name */}

          <div>
            <div>
              <h3 className="text-lg font-semibold mt-1 text-foreground text-gray-800">
                {selectedRule.name}
              </h3>
              {selectedRule.created_by && (
                <div className="text-xs text-gray-600">
                  Created By: {selectedRule.created_by.full_name}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {selectedRule.description || 'No description provided'}
            </div>
          </div>
          {/* Target Details Row */}
          <div className="flex flex-wrap gap-3">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">
                Status
              </Label>
              <div className="mt-1">
                <Badge variant="outline">
                  {selectedRule.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">
                Target Name
              </Label>
              <div className="mt-1">
                <Badge variant="outline">{selectedRule.target_name}</Badge>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">
                Target Type
              </Label>
              <div className="mt-1">
                <Badge variant="outline">
                  {removeUnderscoresAndMakeFirstLetterCapital(
                    selectedRule.target_type_code
                  )}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">
                Approval Type
              </Label>
              <div className="mt-1">
                <Badge variant="outline">
                  {removeUnderscoresAndMakeFirstLetterCapital(
                    selectedRule.approval_type_code
                  )}
                </Badge>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="usage" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="text-sm">Total Triggers</div>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-semibold">
                  {selectedRule.usage_count}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <div className="text-sm">Last Triggered</div>
              </CardHeader>
              <CardContent>
                <div className="captialize text-lg text-gray-800 font-medium">
                  {selectedRule.last_applied_at
                    ? formatDistanceToNow(
                        parseISO(selectedRule.last_applied_at),
                        { addSuffix: true }
                      )
                    : 'Never'}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="history" className="space-y-4">
          <PageLoader query={ruleTriggersQuery}>
            {(ruleTriggers) => {
              return (
                <div className="h-full max-h-[32rem] overflow-y-auto space-y-3">
                  {ruleTriggers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="text-muted-foreground mb-2">
                        <svg
                          className="w-8 h-8 mx-auto"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        No rule triggers found
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        No payment approval rules have been triggered for this
                        item.
                      </p>
                    </div>
                  ) : (
                    ruleTriggers.map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 rounded-lg border border-border bg-card  transition-colors duration-200"
                      >
                        {/* Header with rule name and status */}
                        <div className="flex items-start justify-between ">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-foreground truncate">
                              {rule.rule_name}
                            </h4>
                          </div>
                          <Badge
                            variant="secondary"
                            className="ml-3 flex-shrink-0"
                          >
                            {rule.payment_status}
                          </Badge>
                        </div>

                        {/* Payment type and evaluation time */}
                        <div className="mb-3">
                          <p className="text-sm text-muted-foreground">
                            {rule.payment_type}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Evaluated{' '}
                            {formatDistanceToNow(parseISO(rule.evaluated_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>

                        {/* Target type badge */}
                        <div className="flex items-center">
                          <Badge variant="outline" className="text-xs">
                            {removeUnderscoresAndMakeFirstLetterCapital(
                              rule.rule_target_type
                            )}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )
            }}
          </PageLoader>
        </TabsContent>
      </Tabs>
    </DialogContent>
  )
}
